import React, { useRef, useEffect, useState, useContext } from "react";
import { createMapView } from "../utils/create-map-view";
import gpGetServiceArea from "../utils/query-service-area";
import { queryFeatureCount, queryStatistic } from "../utils/query-statistic";
import { rasterLayer, haltestellenLayer, poiSchoolLayer, poiLebensmittelLayer } from "../utils/feature-layers";
import PopupPortal from "../portals/PopupPortal";
import PopupContent from "./PopupContent";
import splitDirectionAndHour from "../utils/parse-vao-response";
import StateContext from "../contexts/StateContext";
import { useNavigate } from "react-router-dom";

const Map = ({
                 setIfServiceAreaResolved,
                 setIfServiceAreaStatsQueried,
                 serviceAreaCutoffs,
                 setServiceAreaStats,
                 setPoiInServiceArea,
                 departureInfoQueryDate,
                 view,
                 setView
             }) => {
    const navigate = useNavigate();
    // const [view, setView] = useState(null);
    const [popupData, setPopupData] = useState({});
    const mapRef = useRef(null); // so we can access it directly in the DOM
    const [containerToLoad, setContainerToLoad] = useState(null);
    const GlobalState = useContext(StateContext);

    const setContentInfo = (hst_name, query_date, departureInfos) => {
        setPopupData({
            title: hst_name,
            queryDate: query_date,
            chartData: departureInfos
        });
        return containerToLoad;
    };

    const handleDepartureInfoQuery = async (hst_id, departureInfoQueryDate) => {
        try {
            let url = `${process.env.REACT_APP_BASE_URL}/VAO/departureBoard?hst_id=${hst_id}&date=${departureInfoQueryDate}`;
            // let url = `http://ispacevm14.researchstudio.at:5501/departureBoard?hst_id=${hst_id}&${getCurrentDateForRequest()}`
            let res = await fetch(url);
            return res.json();
        } catch (error) {
            console.log(error);
            throw new Error("Failed to fetch departure information.");
        }
    };

    useEffect(() => {
        const popupRoot = document.createElement("div");
        popupRoot.setAttribute("id", "popupRoot");
        setContainerToLoad(popupRoot);
    }, []);

    useEffect(
        () => {
            // what I want to do...
            // create map view
            if (GlobalState.ifSignedIn){
                setView(createMapView(mapRef.current));
            } else {
                // sessionStorage.clear();
                navigate('/');
            }
            setIfServiceAreaStatsQueried(false);

            // clean up to reduce memory leaks
            return () => {
                view && view.destroy();
            };
        },
        [GlobalState.ifSignedIn]
    );

    useEffect(() => {
        if (!view) {
            return;
        }
        const handle = view.on("click", function(evt) {
            gpGetServiceArea(view, evt.mapPoint, serviceAreaCutoffs).then(
                (SAResult) => {
                    setIfServiceAreaStatsQueried(true);
                    // if SA result is an array (=> the query was successful)
                    if (Array.isArray(SAResult)) {
                        setIfServiceAreaResolved(true);
                        const poiQueries = [
                            queryFeatureCount(poiSchoolLayer, SAResult[SAResult.length - 1], "school"),
                            queryFeatureCount(poiLebensmittelLayer, SAResult[SAResult.length - 1], "lebensmittle")
                        ]
                        let queries = SAResult.map((geometry) => queryStatistic(rasterLayer, geometry));
                        const queries_all = queries.concat(poiQueries)
                        Promise.all(queries_all).then((results) => {
                            let hws_arr = [];
                            let tagbev_arr = [];
                            const ifPoisInServiceArea = {school: false, lebensmittel: false}
                            results.forEach(r => {
                                if ("hws_sum" in r) {
                                    hws_arr.push(r["hws_sum"] || 0); // if the value is null then return 0
                                    tagbev_arr.push(r["tagbev_sum"] || 0);
                                } else if ("school" in r){
                                    ifPoisInServiceArea.school = r["school"] > 0;
                                } else if ("lebensmittle" in r) {
                                    ifPoisInServiceArea.lebensmittel = r["lebensmittle"] > 0;
                                }
                            });
                            const serviceAreaCutOffUserInput = serviceAreaCutoffs.filter(cutoff => cutoff > 0);
                            serviceAreaCutOffUserInput.sort((a, b) => a - b); // sort the array in ascending order
                            let query_result = serviceAreaCutOffUserInput.map((cutoff, index) => ({
                                cutoff: cutoff,
                                hws: hws_arr[index],
                                tagbev: tagbev_arr[index],
                            }));
                            setServiceAreaStats(query_result);
                            setPoiInServiceArea(ifPoisInServiceArea);
                        });
                    } else {
                        // when no service area is returned
                        setIfServiceAreaResolved(false);
                        setServiceAreaStats(SAResult); // set stats result to the error message
                    }
                });
        });
        return () => {
            handle && handle.remove();
        };
    }, [view, serviceAreaCutoffs]);

    useEffect(() => {
        let clickHandle;
        if (!view) {
            return;
        }
        if (mapRef.current) {
            view.when(() => {
                view.popupEnabled = false;
                clickHandle = view.on("click", (event) => {
                    const opts = {
                        include: haltestellenLayer
                    };

                    view.hitTest(event, opts).then(async (response) => {
                        if (response.results.length) {
                            const graphic = response.results[0].graphic;
                            view.goTo(graphic); // view goes to the clicked point in the map
                            const hst_id = graphic.attributes["hst_globid"];
                            const hst_name = graphic.attributes["hst_name"];

                            view.openPopup({
                                title: "Haltestellendaten",
                                location: event.mapPoint,
                                content: setContentInfo(hst_name, departureInfoQueryDate,  null)
                            });

                            try {
                                const data = await handleDepartureInfoQuery(hst_id, departureInfoQueryDate);
                                if (data["Departure"]) {
                                    let departureInfos = splitDirectionAndHour(data["Departure"]);
                                    // update the popup info after the data is acquired.
                                    setContentInfo(hst_name, departureInfoQueryDate, departureInfos);
                                } else {
                                    view.openPopup({
                                        title: "Entschuldigung",
                                        location: event.mapPoint,
                                        content: "Leider sind die Abfahrtsinformation für das ausgewählte Datum nicht verfügbar."
                                    });
                                }
                            } catch (error) {
                                view.openPopup({
                                    title: "Fehler",
                                    location: event.mapPoint,
                                    content: "Fehler beim Abrufen der Abfahrtsinformation."
                                });
                            }
                        } else {
                            view.openPopup({
                                title: "Ungültiger Stationsstandort",
                                location: event.mapPoint,
                                content: "Bitte klicken Sie auf einen gültigen Stationspunkt."
                            });
                        }
                    });
                });

            });
        }
        return () => {
            clickHandle && clickHandle.remove();
        };
    }, [mapRef, view, departureInfoQueryDate]);

    return (
         <div className="mapDiv" ref={mapRef}>
            {containerToLoad !== null && Object.keys(popupData).length !== 0 &&
                <PopupPortal mountNode={containerToLoad}>
                    <PopupContent popupInfo={popupData}></PopupContent>
                </PopupPortal>
            }
        </div>);
};

export default Map;
